import { CheckCircleIcon } from '@chakra-ui/icons';
import {
  Badge,
  Button,
  Card,
  HStack,
  Icon,
  Skeleton,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import {
  IdentityType,
  OptionAssetGrantFragment,
  useGetOptionAssetQuery,
} from '@blockpulse3/graphql/hooks';
import { formatDate } from '@blockpulse3/helpers';
import {
  AssetBadge,
  ErrorQueryCard,
  IdentityAvatar,
  TableContainer,
} from '@blockpulse3/ui/commons';
import { useBadge } from '@blockpulse3/ui/ui-hooks';

import { OptionGrantStatus, getOptionAssetGrantStatus } from '../../../utils';

type Props = {
  values?: OptionAssetGrantFragment[];
};

export function OptionSummarySubscribersTable({ values = [] }: Props): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nOptionSubscribersTable = useTranslations('OptionSubscribersTable');

  const { getBadge } = useBadge(
    undefined,
    [
      {
        value: OptionGrantStatus.ATTRIBUTED,
        color: 'gray',
        label: i18nOptionSubscribersTable('Badge.ATTRIBUTED'),
      },
      {
        value: OptionGrantStatus.SUBSCRIBED,
        color: 'blue',
        label: i18nOptionSubscribersTable('Badge.SUBSCRIBED'),
      },
      {
        value: OptionGrantStatus.VESTING,
        color: 'green',
        label: i18nOptionSubscribersTable('Badge.VESTING'),
      },
      {
        value: OptionGrantStatus.CADUC,
        color: 'red',
        label: i18nOptionSubscribersTable('Badge.CADUC'),
      },
    ],
    { color: 'red', label: i18nOptionSubscribersTable('Badge.CADUC') },
  );

  const { assetId = '', companyId = '' } = useParams();

  const { data, loading, error } = useGetOptionAssetQuery({
    variables: {
      getAssetInput: {
        assetId,
        companyId,
      },
    },
  });

  if (loading) {
    return (
      <Skeleton>
        <Card h="200px" />
      </Skeleton>
    );
  }

  if (error) {
    return <ErrorQueryCard h="200px" />;
  }

  if (!data) {
    return <ErrorQueryCard h="200px" />;
  }

  const option = data.getOptionAsset;

  return (
    <TableContainer>
      <Table variant="striped">
        <Thead>
          <Tr>
            <Th>{i18nOptionSubscribersTable('Name')}</Th>
            <Th>{i18nOptionSubscribersTable('Status')}</Th>
            <Th>{i18nOptionSubscribersTable('GrantDate')}</Th>
            <Th>{i18nOptionSubscribersTable('GrantAmount')}</Th>
            <Th />
          </Tr>
        </Thead>
        <Tbody>
          {values.map((grant) => {
            const badge = getBadge(getOptionAssetGrantStatus(grant, option));
            return (
              <Tr key={grant.identity.id}>
                <Td fontWeight="600">
                  <HStack spacing="2.5">
                    <IdentityAvatar identity={grant.identity} />
                    <Stack spacing="1">
                      <HStack spacing="1">
                        <Text fontWeight="600">{grant.identity.name}</Text>
                        {grant.identity.isVerified && (
                          <Icon as={CheckCircleIcon} boxSize="15px" color="green.400" />
                        )}
                        {grant.identity.type === IdentityType.COMPANY && (
                          <Text fontWeight="500">({grant.identity.identifier})</Text>
                        )}
                      </HStack>
                      <Text color="gray.500" fontWeight="400">
                        {grant.identity.identifier}
                      </Text>
                    </Stack>
                  </HStack>
                </Td>
                <Td>
                  <Badge colorScheme={badge.color}>{badge.label}</Badge>
                </Td>
                <Td fontWeight="600">
                  {formatDate(dayjs.unix(grant.date).toDate(), 'D MMMM YYYY')}
                </Td>
                <Td>
                  <AssetBadge value={grant.amount} type={option.assetType} />
                </Td>
                <Td>
                  <Button variant="secondary">{i18nGeneral('DetailsButton')}</Button>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
}

export type OptionSummarySubscribersTableProps = Props;
